import { useEffect, useState, type JSX } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { api, type User } from '../api.js';
import { useAuth } from '../auth.js';
import { Icon } from '../components/Icon.js';
import { Topbar } from './AdminLayout.js';
import { fmtDate, relativeTime } from '../utils/format.js';
import { ROLE_LABEL, isAdminLevel } from '../utils/roles.js';

export function UsersList(): JSX.Element {
  const { user: me } = useAuth();
  const nav = useNavigate();
  const [items, setItems] = useState<User[] | null>(null);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    api.listUsers().then(setItems).catch((e: Error) => setErr(e.message));
  }, []);

  return (
    <>
      <Topbar
        title="Пользователи"
        actions={
          <button
            type="button"
            className="btn btn-primary btn-sm"
            onClick={() => nav('/admin/users/new')}
          >
            <Icon name="plus" size={13} />
            Пригласить
          </button>
        }
      />
      <div style={{ padding: '24px 28px', flex: 1, overflowY: 'auto' }}>
        {err && (
          <div
            className="card"
            style={{
              padding: 14,
              marginBottom: 14,
              color: 'var(--danger-fg)',
              background: 'var(--danger-subtle-bg)',
            }}
          >
            {err}
          </div>
        )}
        <div className="card">
          <table className="tbl">
            <thead>
              <tr>
                <th>Имя</th>
                <th>Email</th>
                <th>Роль</th>
                <th>Профили</th>
                <th>Создан</th>
                <th>Обновлён</th>
              </tr>
            </thead>
            <tbody>
              {items?.map((u) => (
                <tr key={u.id} onClick={() => nav(`/admin/users/${u.id}`)}>
                  <td>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <div
                        style={{
                          width: 28,
                          height: 28,
                          borderRadius: '50%',
                          background: 'var(--bg-sunken)',
                          display: 'grid',
                          placeItems: 'center',
                          fontSize: 12,
                          fontWeight: 600,
                          color: 'var(--fg-muted)',
                        }}
                      >
                        {(u.name || u.email)[0]?.toUpperCase() ?? '?'}
                      </div>
                      <div style={{ fontWeight: 500 }}><bdi>{u.name || '—'}</bdi></div>
                      {u.id === me?.id && (
                        <span
                          className="chip no-dot chip-info"
                          style={{ fontSize: 10.5, height: 18, padding: '0 6px' }}
                        >
                          вы
                        </span>
                      )}
                    </div>
                  </td>
                  <td>
                    <span className="subtle" style={{ fontSize: 12.5 }}>
                      {u.email}
                    </span>
                  </td>
                  <td>
                    <span
                      className={
                        'chip no-dot ' + (isAdminLevel(u.role) ? 'chip-info' : 'chip-neutral')
                      }
                    >
                      {ROLE_LABEL[u.role]}
                    </span>
                  </td>
                  <td>
                    {u.role === 'superadmin' ? (
                      <span className="subtle" style={{ fontSize: 12.5 }}>
                        все
                      </span>
                    ) : u.role === 'volunteer' ? (
                      <span className="subtle" style={{ fontSize: 12.5 }}>
                        только чек-ин
                      </span>
                    ) : (
                      <span style={{ fontSize: 12.5 }}>
                        {u.allowedProfileIds.length + u.otherProfilesCount === 0
                          ? <span style={{ color: 'var(--danger-fg)' }}>нет доступа</span>
                          : `${u.allowedProfileIds.length}`}
                        {u.otherProfilesCount > 0 && (
                          <span className="subtle"> + {u.otherProfilesCount} у других админов</span>
                        )}
                      </span>
                    )}
                  </td>
                  <td>
                    <span className="subtle" style={{ fontSize: 12.5 }}>
                      {fmtDate(u.createdAt)}
                    </span>
                  </td>
                  <td>
                    <span className="subtle" style={{ fontSize: 12.5 }}>
                      {relativeTime(u.updatedAt)}
                    </span>
                  </td>
                </tr>
              ))}
              {items && items.length === 0 && (
                <tr className="no-hover">
                  <td colSpan={6} style={{ textAlign: 'center', padding: 32, color: 'var(--fg-subtle)' }}>
                    Пользователей пока нет.{' '}
                    <Link to="/admin/users/new" style={{ textDecoration: 'underline' }}>
                      Пригласить первого
                    </Link>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}
